import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { auth } from '../services/auth';
import {
  capUpperTreeIdsFromConfig,
  isJnet2CapDevice,
  radioProfileNeedsCapTreeSelect,
  showApiWarnings,
  ssidBandNeedsCapTreeSelect,
} from '../utils/publicWifiRrmPlanUi';
import { formatRadioProfileOptionLabel } from '../utils/radioProfileBand';
import { formatSsidProfileOptionLabel } from '../utils/ssidProfileLabel';

function profilesAssignedToDevice(profiles, serial) {
  const key = String(serial || '').toLowerCase();
  if (!key) return [];
  return (profiles || []).filter((p) =>
    (p.device_serials || []).some((ds) => String(ds).toLowerCase() === key),
  );
}

function summarizeApplyResults(enqueueSnackbar, label, applyResults) {
  if (!Array.isArray(applyResults) || applyResults.length === 0) {
    enqueueSnackbar(`${label} applied`, { variant: 'success' });
    return;
  }
  const failed = applyResults.filter((r) => r && r.success === false);
  const succeeded = applyResults.filter((r) => r && r.success !== false);
  if (failed.length === 0) {
    enqueueSnackbar(`${label} applied`, { variant: 'success' });
    return;
  }
  const detail = failed
    .map((r) => r.error || r.message || r.serial || 'failed')
    .slice(0, 3)
    .join('; ');
  enqueueSnackbar(
    `${label}: ${succeeded.length} ok, ${failed.length} failed${detail ? ` (${detail})` : ''}`,
    { variant: failed.length === applyResults.length ? 'error' : 'warning', autoHideDuration: 10000 },
  );
}

/**
 * Per-device Public WiFi assign UI — mirrors Wi-Fi Network → Configuration
 * (SSID / radio profile apply with optional CAP tree_id).
 */
export default function DevicePublicWifiConfigPanel({ device, serial, onApplied }) {
  const { enqueueSnackbar } = useSnackbar();
  const canWrite = auth.canWritePublicWifi();
  const serialLower = String(serial || device?.serial_number || '').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [ssidProfiles, setSsidProfiles] = useState([]);
  const [radioProfiles, setRadioProfiles] = useState([]);
  const [vlanProfiles, setVlanProfiles] = useState([]);
  const [selectedSsidId, setSelectedSsidId] = useState('');
  const [selectedRadioId, setSelectedRadioId] = useState('');
  const [capTreeIds, setCapTreeIds] = useState([]);
  const [ssidTreeId, setSsidTreeId] = useState('');
  const [radioTreeId, setRadioTreeId] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [assigningSsid, setAssigningSsid] = useState(false);
  const [assigningRadio, setAssigningRadio] = useState(false);
  const [unassigningId, setUnassigningId] = useState(null);

  const isCap = useMemo(() => isJnet2CapDevice(device), [device]);

  const selectedSsid = useMemo(
    () => ssidProfiles.find((p) => p.id === selectedSsidId) || null,
    [ssidProfiles, selectedSsidId],
  );
  const selectedRadio = useMemo(
    () => radioProfiles.find((p) => p.id === selectedRadioId) || null,
    [radioProfiles, selectedRadioId],
  );

  const assignedSsids = useMemo(
    () => profilesAssignedToDevice(ssidProfiles, serialLower),
    [ssidProfiles, serialLower],
  );
  const assignedRadios = useMemo(
    () => profilesAssignedToDevice(radioProfiles, serialLower),
    [radioProfiles, serialLower],
  );

  const ssidNeedsTree = useMemo(
    () => isCap && selectedSsid && ssidBandNeedsCapTreeSelect(selectedSsid.band),
    [isCap, selectedSsid],
  );
  const radioNeedsTree = useMemo(
    () => isCap && selectedRadio && radioProfileNeedsCapTreeSelect(selectedRadio.band),
    [isCap, selectedRadio],
  );
  const cap24Blocked = useMemo(
    () => isCap && selectedSsid && String(selectedSsid.band || '').toLowerCase() === '2.4ghz',
    [isCap, selectedSsid],
  );

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [ssidsRes, radiosRes, vlansRes] = await Promise.all([
        api.getSSIDProfiles(),
        api.getRadioProfiles(),
        api.getVLANProfiles().catch(() => ({})),
      ]);
      const ssidList = Array.isArray(ssidsRes) ? ssidsRes : (ssidsRes?.ssids || []);
      const radioList = Array.isArray(radiosRes) ? radiosRes : (radiosRes?.radio_profiles || []);
      const vlanList = Array.isArray(vlansRes) ? vlansRes : (vlansRes?.vlans || []);
      setSsidProfiles(ssidList);
      setRadioProfiles(radioList);
      setVlanProfiles(vlanList);

      const assignedS = profilesAssignedToDevice(ssidList, serialLower);
      const assignedR = profilesAssignedToDevice(radioList, serialLower);
      setSelectedSsidId(assignedS[0]?.id || '');
      setSelectedRadioId(assignedR[0]?.id || '');
    } catch (err) {
      setLoadError(err?.message || 'Failed to load Public WiFi profiles');
    } finally {
      setLoading(false);
    }
  }, [serialLower]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Load CAP upper tree_ids when needed for SSID or radio assign.
  useEffect(() => {
    if (!isCap || (!ssidNeedsTree && !radioNeedsTree)) {
      if (!isCap) {
        setCapTreeIds([]);
        setSsidTreeId('');
        setRadioTreeId('');
      }
      return undefined;
    }
    let cancelled = false;
    setConfigLoading(true);
    api.getDeviceConfig(serialLower)
      .then((resp) => {
        if (cancelled) return;
        const ids = capUpperTreeIdsFromConfig(resp?.configuration);
        setCapTreeIds(ids);
        const savedSsid = selectedSsid?.device_tree_ids?.[serialLower];
        const savedRadio = selectedRadio?.device_tree_ids?.[serialLower];
        setSsidTreeId(savedSsid || ids[0] || '');
        setRadioTreeId(savedRadio || ids[0] || '');
      })
      .catch(() => {
        if (!cancelled) {
          setCapTreeIds([]);
        }
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [isCap, ssidNeedsTree, radioNeedsTree, serialLower, selectedSsid?.device_tree_ids, selectedRadio?.device_tree_ids]);

  const handleApplySsid = async () => {
    if (!canWrite || !selectedSsidId || !serialLower) return;
    if (cap24Blocked) {
      enqueueSnackbar('CAP devices have no 2.4 GHz radio', { variant: 'warning' });
      return;
    }
    if (ssidNeedsTree && !ssidTreeId) {
      enqueueSnackbar('Select a CAP radio slot (tree_id) for this SSID', { variant: 'warning' });
      return;
    }
    setAssigningSsid(true);
    try {
      const res = await api.assignSSIDToDevice(selectedSsidId, serialLower, {
        treeId: ssidNeedsTree ? ssidTreeId : undefined,
      });
      showApiWarnings(enqueueSnackbar, res);
      summarizeApplyResults(enqueueSnackbar, 'SSID profile', res?.apply_results);
      await loadProfiles();
      if (typeof onApplied === 'function') await onApplied();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to assign SSID profile', { variant: 'error' });
    } finally {
      setAssigningSsid(false);
    }
  };

  const handleApplyRadio = async () => {
    if (!canWrite || !selectedRadioId || !serialLower) return;
    if (radioNeedsTree && !radioTreeId) {
      enqueueSnackbar('Select a CAP radio slot (tree_id) for this radio profile', { variant: 'warning' });
      return;
    }
    setAssigningRadio(true);
    try {
      const res = await api.assignRadioProfileToDevice(selectedRadioId, serialLower, {
        treeId: radioNeedsTree ? radioTreeId : undefined,
      });
      showApiWarnings(enqueueSnackbar, res);
      summarizeApplyResults(enqueueSnackbar, 'Radio profile', res?.apply_results);
      await loadProfiles();
      if (typeof onApplied === 'function') await onApplied();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to assign radio profile', { variant: 'error' });
    } finally {
      setAssigningRadio(false);
    }
  };

  const handleUnassignSsid = async (ssidId) => {
    if (!canWrite || !ssidId || !serialLower) return;
    setUnassigningId(`ssid:${ssidId}`);
    try {
      await api.unassignSSIDFromDevice(ssidId, serialLower);
      enqueueSnackbar('SSID profile unassigned', { variant: 'success' });
      if (selectedSsidId === ssidId) setSelectedSsidId('');
      await loadProfiles();
      if (typeof onApplied === 'function') await onApplied();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to unassign SSID', { variant: 'error' });
    } finally {
      setUnassigningId(null);
    }
  };

  const handleUnassignRadio = async (profileId) => {
    if (!canWrite || !profileId || !serialLower) return;
    setUnassigningId(`radio:${profileId}`);
    try {
      await api.unassignRadioProfileFromDevice(profileId, serialLower);
      enqueueSnackbar('Radio profile unassigned', { variant: 'success' });
      if (selectedRadioId === profileId) setSelectedRadioId('');
      await loadProfiles();
      if (typeof onApplied === 'function') await onApplied();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to unassign radio profile', { variant: 'error' });
    } finally {
      setUnassigningId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity="error" action={<Button onClick={loadProfiles}>Retry</Button>}>
        {loadError}
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Assign Public WiFi SSID and radio profiles to this device using the same
        profile apply path as Wi-Fi Network → Configuration. Tree-management
        Fields mode does not change Public WiFi.
      </Alert>

      {!canWrite && (
        <Alert severity="warning">
          You have read-only Public WiFi access. Profile assignment is disabled.
        </Alert>
      )}

      {(assignedSsids.length > 0 || assignedRadios.length > 0) && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Currently assigned</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {assignedSsids.map((p) => (
              <Chip
                key={`ssid-${p.id}`}
                icon={<WifiIcon />}
                label={formatSsidProfileOptionLabel(p, vlanProfiles)}
                onDelete={canWrite ? () => handleUnassignSsid(p.id) : undefined}
                disabled={unassigningId === `ssid:${p.id}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
            {assignedRadios.map((p) => (
              <Chip
                key={`radio-${p.id}`}
                icon={<SettingsInputAntennaIcon />}
                label={formatRadioProfileOptionLabel(p)}
                onDelete={canWrite ? () => handleUnassignRadio(p.id) : undefined}
                disabled={unassigningId === `radio:${p.id}`}
                color="secondary"
                variant="outlined"
                size="small"
              />
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>SSID profile</Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small" disabled={!canWrite}>
            <InputLabel>SSID profile</InputLabel>
            <Select
              label="SSID profile"
              value={selectedSsidId}
              onChange={(e) => setSelectedSsidId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {ssidProfiles.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {formatSsidProfileOptionLabel(p, vlanProfiles)}
                  {p.band ? ` (${p.band})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {cap24Blocked && (
            <Alert severity="warning">CAP devices have no 2.4 GHz Public WiFi radio.</Alert>
          )}

          {ssidNeedsTree && (
            <FormControl fullWidth size="small" disabled={!canWrite || configLoading}>
              <InputLabel>CAP radio slot (tree_id)</InputLabel>
              <Select
                label="CAP radio slot (tree_id)"
                value={ssidTreeId}
                onChange={(e) => setSsidTreeId(e.target.value)}
              >
                {capTreeIds.length === 0 && (
                  <MenuItem value="" disabled>
                    <em>{configLoading ? 'Loading…' : 'No 5 GHz slots found'}</em>
                  </MenuItem>
                )}
                {capTreeIds.map((id) => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Required for 5/6 GHz SSIDs on jnet2 CAP devices.
              </FormHelperText>
            </FormControl>
          )}

          <Box>
            <Button
              variant="contained"
              startIcon={<WifiIcon />}
              onClick={handleApplySsid}
              disabled={
                !canWrite
                || !selectedSsidId
                || assigningSsid
                || cap24Blocked
                || (ssidNeedsTree && !ssidTreeId)
              }
            >
              {assigningSsid ? 'Applying…' : 'Apply SSID Profile'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Radio profile</Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small" disabled={!canWrite}>
            <InputLabel>Radio profile</InputLabel>
            <Select
              label="Radio profile"
              value={selectedRadioId}
              onChange={(e) => setSelectedRadioId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {radioProfiles.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {formatRadioProfileOptionLabel(p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {radioNeedsTree && (
            <FormControl fullWidth size="small" disabled={!canWrite || configLoading}>
              <InputLabel>CAP radio slot (tree_id)</InputLabel>
              <Select
                label="CAP radio slot (tree_id)"
                value={radioTreeId}
                onChange={(e) => setRadioTreeId(e.target.value)}
              >
                {capTreeIds.length === 0 && (
                  <MenuItem value="" disabled>
                    <em>{configLoading ? 'Loading…' : 'No 5 GHz slots found'}</em>
                  </MenuItem>
                )}
                {capTreeIds.map((id) => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Required for 5/6 GHz (or all-band) radio profiles on jnet2 CAP devices.
              </FormHelperText>
            </FormControl>
          )}

          <Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SettingsInputAntennaIcon />}
              onClick={handleApplyRadio}
              disabled={
                !canWrite
                || !selectedRadioId
                || assigningRadio
                || (radioNeedsTree && !radioTreeId)
              }
            >
              {assigningRadio ? 'Applying…' : 'Apply Radio Profile'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
}
